import { Link } from "react-router-dom";
import { creators } from "@/mocks/creators";

type Creator = (typeof creators)[number];

interface CompareCardProps {
  creator: Creator;
  summary?: boolean;
}

export default function CompareCard({ creator, summary = false }: CompareCardProps) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors duration-300 hover:border-primary-300">
      <Link
        to={`/detail/${creator.slug}`}
        aria-label={`查看${creator.name}的形象对比详情`}
        className="relative block aspect-[4/5] w-full cursor-pointer overflow-hidden bg-background-200"
      >
        <div className="absolute inset-y-0 left-0 w-1/2 overflow-hidden">
          <img
            src={creator.beforeImage}
            alt={`${creator.name} ${creator.beforeYear} 年早期形象`}
            title={`${creator.name} 早期形象对比`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>
        <div className="absolute inset-y-0 right-0 w-1/2 overflow-hidden">
          <img
            src={creator.afterImage}
            alt={`${creator.name} ${creator.afterYear} 年近期形象`}
            title={`${creator.name} 近期形象对比`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>
        <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-background-50/90"></span>

        <span className="absolute left-2 top-2 rounded-full bg-foreground-950/70 px-2.5 py-1 text-[11px] font-medium text-background-50">
          {creator.beforeYear}
        </span>
        <span className="absolute right-2 top-2 rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
          {creator.afterYear}
        </span>
        <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-background-50/90 px-2.5 py-1 text-[11px] font-medium text-foreground-900">
          <i className="ri-fire-line text-primary-500"></i>
          反差 {creator.contrastScore}
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-heading text-base font-bold text-foreground-950 md:text-lg">
            <Link to={`/detail/${creator.slug}`} className="cursor-pointer hover:text-primary-600">
              {creator.name}
            </Link>
          </h3>
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] text-secondary-900">
            {creator.platform}
          </span>
          <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] text-accent-900">
            {creator.category}
          </span>
        </div>

        {summary && (
          <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">
            {creator.summary}
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {creator.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-md border border-background-300 px-2 py-1 text-[11px] text-foreground-600"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-auto flex items-center justify-between pt-4">
          <span className="flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-bar-chart-line text-sm text-secondary-500"></i>
            热度 {creator.heat}
          </span>
          <Link
            to={`/detail/${creator.slug}`}
            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            看对比
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}